import type { LucideIcon } from "@/components/icons"

import {
  BookOpen,
  Bot,
  Brain,
  Clock,
  Cloud,
  Database,
  GitBranch,
  Globe,
  Mail,
  Merge,
  MessageSquarePlus,
  MousePointerClick,
  Paperclip,
  Play,
  Plug,
  Repeat,
  ShieldCheck,
  ShieldQuestion,
  Split,
  Square,
  Type,
} from "@/components/icons"

export type ComponentItem = {
  type: string
  label: string
  icon: LucideIcon
  category: "flow" | "ai" | "data" | "tools"
  borderColor: string
  data?: Record<string, unknown>
  disabled?: boolean
  disabledReason?: string
  comingSoon?: boolean
}

export const WORKFLOW_COMPONENTS: ComponentItem[] = [
  {
    type: "tool",
    label: "Plugin",
    icon: Plug,
    category: "tools",
    borderColor: "border-border",
    data: { toolName: "plugin_execute", config: {}, plugin_bindings: {} },
  },
  {
    type: "start",
    label: "Start",
    icon: Play,
    category: "flow",
    borderColor: "border-border",
  },
  {
    type: "end",
    label: "End",
    icon: Square,
    category: "flow",
    borderColor: "border-border",
  },
  {
    type: "conditional",
    label: "Conditional",
    icon: GitBranch,
    category: "flow",
    borderColor: "border-border",
  },
  {
    type: "loopContainer",
    label: "Loop",
    icon: Repeat,
    category: "flow",
    borderColor: "border-border",
    data: {
      mode: "count",
      iterations: 3,
      resultMode: "collect",
      joinSeparator: "\\n",
      width: 500,
      height: 250,
    },
  },
  {
    type: "parallel",
    label: "Parallel",
    icon: Split,
    category: "flow",
    borderColor: "border-border",
  },
  {
    type: "merge",
    label: "Merge",
    icon: Merge,
    category: "flow",
    borderColor: "border-border",
  },
  {
    type: "delay",
    label: "Delay",
    icon: Clock,
    category: "flow",
    borderColor: "border-border",
    comingSoon: true,
  },
  {
    type: "llm",
    label: "LLM Processor",
    icon: Brain,
    category: "ai",
    borderColor: "",
    data: { label: "LLM Processor" },
  },
  {
    type: "agent",
    label: "Agent (Autonomous)",
    icon: Bot,
    category: "ai",
    borderColor: "border-border",
    data: { label: "Agent" },
  },
  {
    type: "variable",
    label: "Variable",
    icon: Database,
    category: "data",
    borderColor: "border-border",
  },
  {
    type: "format",
    label: "Format",
    icon: Type,
    category: "data",
    borderColor: "border-border",
    data: { template: "" },
  },
  {
    type: "email",
    label: "Email",
    icon: Mail,
    category: "tools",
    borderColor: "border-border",
    data: { to: "", subject: "", bodyTemplate: "" },
  },
  {
    type: "factcheck",
    label: "Fact-Check",
    icon: ShieldCheck,
    category: "ai",
    borderColor: "border-border",
    data: { claim: "", criteria: "" },
  },
  {
    type: "userInput",
    label: "User Input",
    icon: MessageSquarePlus,
    category: "data",
    borderColor: "border-border",
    data: { promptMessage: "" },
  },
  {
    type: "approval",
    label: "Approval",
    icon: ShieldQuestion,
    category: "flow",
    borderColor: "border-border",
    data: { promptMessage: "" },
  },
  {
    type: "attachment",
    label: "Attachment",
    icon: Paperclip,
    category: "data",
    borderColor: "border-border",
    data: { sourceType: "upload" },
  },
  {
    type: "tool",
    label: "Web Search",
    icon: Globe,
    category: "tools",
    borderColor: "border-border",
    data: {
      label: "Web Search",
      toolName: "web_search",
      description: "Search the internet for information",
    },
  },
  {
    type: "tool",
    label: "Library Search",
    icon: BookOpen,
    category: "tools",
    borderColor: "border-border",
    data: {
      label: "Library Search",
      toolName: "library_search",
      description: "Search knowledge libraries",
    },
  },
  {
    type: "tool",
    label: "Web Scraper",
    icon: MousePointerClick,
    category: "tools",
    borderColor: "border-border",
    data: {
      label: "Web Scraper",
      toolName: "web_scrape",
      description: "Extract content from webpages",
    },
  },
  {
    type: "tool",
    label: "Microsoft 365",
    icon: Cloud,
    category: "tools",
    borderColor: "border-border",
    data: {
      label: "Microsoft 365",
      toolName: "microsoft_365_search",
      description: "Search Emails and Files",
    },
  },
]

export const CATEGORY_LABELS: Record<ComponentItem["category"], string> = {
  flow: "Flow Control",
  ai: "AI",
  data: "Data & Utilities",
  tools: "Tools",
}

export const CATEGORY_ORDER: ComponentItem["category"][] = ["flow", "ai", "data", "tools"]
